import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ICheckpointGroup, ICheckpointQRCodeExportData, ICheckpointV2 } from '@fi-shared/checklist/checklist-v2';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import {
    FiChecklistHelperService,
    FiChecklistModuleDiscardChangesDialogData,
} from '@fi-shared/checklist/fi-checklist-helper.service';
import { flattenSimple } from '@fi-shared/utils/flatten-simple';
import {
    FiToggleSwitchLabelAlign,
    FiToggleSwitchSliderColorClass,
} from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { FiChecklistStoreService } from '@fi-shared/checklist/fi-checklist-store.service';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';
import { Subject } from 'rxjs';

export interface IFiChecklist2QRCodesDialogData {
    isQRCodeEnabled: boolean;
    checkpointGroups: ICheckpointGroup[];
}

export interface IFiChecklist2QRCodesDialogResult {
    newIsQRCodeEnabled: boolean;
}

@Component({
    selector: 'fi-checklist2-qr-codes-dialog',
    templateUrl: './qr-codes-dialog.component.html',
    styleUrls: ['./qr-codes-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist2-qr-codes-dialog' },
    standalone: false,
})
export class FiChecklist2QRCodesDialogComponent
    extends FiDialogsContentBase<IFiChecklist2QRCodesDialogData, IFiChecklist2QRCodesDialogResult>
    implements OnInit, OnDestroy
{
    get isQRCodeEnabled(): boolean {
        return this.data.isQRCodeEnabled;
    }
    set isQRCodeEnabled(value: boolean) {
        this.data.isQRCodeEnabled = value;
    }

    private fiValuesComparison: FiValuesComparison<boolean>;
    allCheckpoints: ICheckpointV2[] = [];

    ngOnInit(): void {
        this.setServices();
        this.allCheckpoints = this.getAllCheckpoints();
        this.fiValuesComparison = new FiValuesComparison(() => this.isQRCodeEnabled);
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);
    }

    isQrCodesListShown(): boolean {
        return this.allCheckpoints.length && this.isQRCodeEnabled;
    }

    getCheckpointName(checkpoint: ICheckpointV2): string {
        return (
            this.getCurrentContentLanguageLabelModelValue(checkpoint.Names) ||
            this.getPrioritizedContentLanguageLabelValue(checkpoint.Names)
        );
    }

    private getPrioritizedContentLanguageLabelValue(contentLanguageLabelModels: IContentLanguageLabelModel[]): string {
        return this.fiContentLanguageSharedService.getPrioritizedContentLanguageLabelValue({
            contentLanguageLabelModels: contentLanguageLabelModels,
            currentSelectedLanguageId: this.fiChecklistStoreService.selectedLanguageId,
            isLanguageNameShown: true,
        });
    }

    private getCurrentContentLanguageLabelModelValue(contentLanguageLabelModels: IContentLanguageLabelModel[]): string {
        return this.fiChecklistHelperService.getCurrentContentLanguageLabelModel(contentLanguageLabelModels);
    }

    getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged());
    }

    getResult(): IFiChecklist2QRCodesDialogResult {
        return {
            newIsQRCodeEnabled: this.isQRCodeEnabled,
        };
    }

    getQRCodeData(checkpoint: ICheckpointV2) {
        return this.fiCheckpoint2HelperService.getQRCodeForCheckpoint(checkpoint);
    }

    exportAllQRCodes() {
        const data: ICheckpointQRCodeExportData[] = this.getQRCodeDataForExport(this.allCheckpoints);
        this.fiChecklistService.exportCheckpointQRCodes(data);
    }

    private _shownQrCodesCheckpointIndexes: number[] = [];
    isQrCodeShown(checkpointIndex: number): boolean {
        return this._shownQrCodesCheckpointIndexes.includes(checkpointIndex);
    }

    toggleShowCode(checkpointIndex: number) {
        if (this._shownQrCodesCheckpointIndexes.includes(checkpointIndex)) {
            const start: number = this._shownQrCodesCheckpointIndexes.indexOf(checkpointIndex);
            this._shownQrCodesCheckpointIndexes.splice(start, 1);
        } else {
            this._shownQrCodesCheckpointIndexes.push(checkpointIndex);
        }
    }

    getButtonTextId(checkpointIndex: number): string {
        return this.isQrCodeShown(checkpointIndex) ? 'qr_common.hide_code' : 'qr_common.show_code'; // 'Hide code' : 'Show code';
    }

    exportQRCode(checkpoint: ICheckpointV2) {
        const data = this.getQRCodeDataForExport([checkpoint]);
        this.fiChecklistService.exportCheckpointQRCodes(data);
    }

    printQRCode(checkpoint: ICheckpointV2) {
        const data = this.getQRCodeDataForExport([checkpoint]);
        this.fiChecklistService.printCheckpointQRCodesPdf(data);
    }

    private getAllCheckpoints(): ICheckpointV2[] {
        return flattenSimple(this.data.checkpointGroups.map((checkpointGroup) => checkpointGroup.Checkpoints));
    }

    private getQRCodeDataForExport(checkpoints: ICheckpointV2[]): ICheckpointQRCodeExportData[] {
        const data = checkpoints.map((checkpoint) => ({
            Name: checkpoint.Name,
            Description: checkpoint.Description,
            QRCodeData: this.getQRCodeData(checkpoint),
        }));
        return data;
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    private fiChecklistStoreService: FiChecklistStoreService;
    private fiContentLanguageSharedService: FiContentLanguageSharedService;
    private fiChecklistService: FiChecklist2Service;
    private fiChecklistHelperService: FiChecklistHelperService;
    private fiCheckpoint2HelperService: FiCheckpoint2HelperService;
    private fiDialogsService: FiDialogsService;

    private setServices() {
        this.fiChecklistService = this.injector.get(FiChecklist2Service);
        this.fiChecklistHelperService = this.injector.get(FiChecklistHelperService);
        this.fiCheckpoint2HelperService = this.injector.get(FiCheckpoint2HelperService);
        this.fiChecklistStoreService = this.injector.get(FiChecklistStoreService);
        this.fiContentLanguageSharedService = this.injector.get(FiContentLanguageSharedService);
        this.fiDialogsService = this.injector.get(FiDialogsService);
    }

    protected readonly FiToggleSwitchLabelAlign = FiToggleSwitchLabelAlign;
    protected readonly FiToggleSwitchSliderColorClass = FiToggleSwitchSliderColorClass;
}
